import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { notification } from "antd";
import { 
  StreamApiClient, 
  BrowserPublisher, 
  STREAM_CONFIG 
} from "../../services/streamService";

function BroadcastPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get streamId from navigation state (passed from login page after create/activate)
  const streamIdFromState = location.state?.streamId;
  
  // Stream state
  const [streamId, setStreamId] = useState(streamIdFromState || null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  
  // Refs
  const videoRef = useRef(null);
  const apiClientRef = useRef(null);
  const publisherRef = useRef(null);
  const viewerIntervalRef = useRef(null);

  // Initialize stream on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    apiClientRef.current = new StreamApiClient();
    publisherRef.current = new BrowserPublisher();

    // Request camera preview immediately
    initCameraPreview();

    return () => {
      // Cleanup on unmount
      stopStream();
      if (viewerIntervalRef.current) {
        clearInterval(viewerIntervalRef.current);
      }
    };
  }, []);

  // Initialize camera preview (before streaming)
  const initCameraPreview = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: STREAM_CONFIG.video.width },
          height: { ideal: STREAM_CONFIG.video.height },
          frameRate: { ideal: STREAM_CONFIG.video.framerate },
          facingMode: 'environment',
        },
        audio: true,
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error("Failed to access camera:", error);
      notification.error({
        message: "Camera Access Failed",
        description: "Please grant camera and microphone permissions.",
        placement: "top",
      });
    }
  };

  // Start publishing (stream already created/activated in login page)
  const startStream = async () => {
    if (!streamId || isLoading) {
      notification.error({
        message: "No Stream ID",
        description: "Stream was not properly created. Please go back and try again.",
        placement: "top",
      });
      return;
    }

    setIsLoading(true);
    try {
      // Just start publishing - stream already created/activated
      const wsUrl = apiClientRef.current.getPublishWsUrl(streamId);
      console.log('[Broadcast] Starting publish to:', wsUrl);
      await publisherRef.current.startPublishing(wsUrl);

      // Update video preview
      if (videoRef.current && publisherRef.current.getPreviewSource()) {
        videoRef.current.srcObject = publisherRef.current.getPreviewSource();
      }

      setIsStreaming(true);

      // Start polling viewer count
      viewerIntervalRef.current = setInterval(async () => {
        const count = await apiClientRef.current.getViewerCount(streamId);
        setViewerCount(count);
      }, 5000);

      notification.success({
        message: "Publishing Started",
        description: "You are now live!",
        placement: "top",
      });
    } catch (error) {
      console.error("Failed to start publishing:", error);
      notification.error({
        message: "Failed to Start Publishing",
        description: error.message,
        placement: "top",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Stop streaming
  const stopStream = async () => {
    if (publisherRef.current) {
      publisherRef.current.stopPublishing();
    }

    if (streamId && apiClientRef.current) {
      try {
        await apiClientRef.current.deactivateStream(streamId);
      } catch (e) {
        console.warn("Failed to deactivate stream:", e);
      }
    }

    if (viewerIntervalRef.current) {
      clearInterval(viewerIntervalRef.current);
      viewerIntervalRef.current = null;
    }

    setIsStreaming(false);
    setStreamId(null);
    setViewerCount(0);
  };

  // Handle stop button click
  const handleStop = async () => {
    await stopStream();
    navigate(-1); // Go back
  };

  // Toggle mute
  const handleMuteToggle = () => {
    if (publisherRef.current) {
      const muted = publisherRef.current.toggleMute();
      setIsMuted(muted);
    }
  };

  // Flip camera
  const handleFlipCamera = async () => {
    if (publisherRef.current) {
      await publisherRef.current.flipCamera();
    }
  };

  // Toggle flashlight (if available)
  const handleFlashlight = async () => {
    if (publisherRef.current?.mediaStream) {
      const videoTrack = publisherRef.current.mediaStream.getVideoTracks()[0];
      if (videoTrack) {
        try {
          const capabilities = videoTrack.getCapabilities();
          if (capabilities.torch) {
            const settings = videoTrack.getSettings();
            await videoTrack.applyConstraints({
              advanced: [{ torch: !settings.torch }]
            });
          } else {
            notification.warning({
              message: "Flashlight not available",
              placement: "top",
            });
          }
        } catch (e) {
          console.warn("Flashlight control failed:", e);
        }
      }
    }
  };

  // Copy viewer link to clipboard
  const handleCopyLink = () => {
    const viewerUrl = `${window.location.origin}/stream/view/${roomId}`;
    navigator.clipboard.writeText(viewerUrl).then(() => {
      notification.success({
        message: "Link Copied!",
        description: viewerUrl,
        placement: "top",
      });
    }).catch((e) => {
      console.warn("Failed to copy link:", e);
      notification.error({
        message: "Failed to copy link",
        placement: "top",
      });
    });
  };

  return (
    <div className="broadcast-container">
      {/* Video Preview */}
      <video
        ref={videoRef}
        className="broadcast-video"
        autoPlay
        muted
        playsInline
      />

      {/* Overlay UI */}
      <div className="broadcast-overlay">
        {/* Top bar */}
        <div className="broadcast-top-bar">
          {/* Live badge */}
          {isStreaming && (
            <div className="live-badge-container">
              <div className="live-badge">
                <span className="live-dot"></span>
                LIVE
              </div>
              <div className="viewer-count">{viewerCount} Viewers</div>
            </div>
          )}
          
          {/* Logo */}
          <div className="broadcast-logo">
            <span className="logo-ball">🎱</span>
          </div>
        </div>

        {/* Chat/Events area placeholder */}
        <div className="broadcast-chat">
          <div className="chat-message">Admin: Stream started.</div>
          <div className="chat-message">Viewer22: Let's go!</div>
        </div>

        {/* Bottom controls */}
        <div className="broadcast-controls">
          <button 
            className="control-button"
            onClick={handleFlipCamera}
            disabled={!isStreaming}
          >
            <span className="control-icon">🔄</span>
            <span className="control-label">Flip Camera</span>
          </button>

          <button 
            className={`control-button ${isMuted ? 'active' : ''}`}
            onClick={handleMuteToggle}
            disabled={!isStreaming}
          >
            <span className="control-icon">{isMuted ? '🔇' : '🎤'}</span>
            <span className="control-label">Mute Microphone</span>
          </button>

          {/* Main action button */}
          {!isStreaming ? (
            <button 
              className="stream-action-button start"
              onClick={startStream}
              disabled={isLoading}
            >
              {isLoading ? 'Starting...' : 'START'}
            </button>
          ) : (
            <button 
              className="stream-action-button stop"
              onClick={handleStop}
            >
              STOP
            </button>
          )}

          <button 
            className="control-button"
            onClick={handleFlashlight}
            disabled={!isStreaming}
          >
            <span className="control-icon">🔦</span>
            <span className="control-label">Flashlight</span>
          </button>

          <button 
            className="control-button share-button"
            onClick={handleCopyLink}
          >
            <span className="control-icon">🔗</span>
            <span className="control-label">Copy Link</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default BroadcastPage;
